import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { PageTitle } from 'common/components/PageTitle';
import {
    IPortfolioItemMatrixListItemFragment,
    CurrentUserDocument,
} from 'graphql/types';
import { IUserProfileSectionProps } from 'user/types';
import { ExtraCategoryLabels } from 'organisation/components/ExtraCategoryLabels';
import { ChipGroup } from 'common/components/Chip';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { Pagination } from 'common/components/Pagination';
import { useSkillGroupsList } from 'skill/hooks/useSkillGroupsList';
import { BoxLoader } from 'common/components/Loader';
import { NoResults } from 'common/components/NoResults';
import { DEFAULT_PAGINATION_SETTINGS } from 'common/hooks/usePagination';
import { useSkillReportList } from 'skill/hooks/useSkillReportList';
import { SkillCard } from 'skill/components/SkillCard';
import { EView, ViewToggle } from 'common/components/ViewToggle';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { List } from 'common/components/List';
import { SkillPortfolioItemListItem } from 'skill/components/SkillPortfolioItemListItem';

const STORAGE_KEY = 'portfolioSkillsView';

export const PortfolioSkills = ({ name }: IUserProfileSectionProps) => {
    const { data } = useQuery(CurrentUserDocument);
    const [translate] = useTranslation();
    const { id: userIdParam } = useParams();
    const { openGlobalDrawer } = useGlobalDrawer();
    const location = useLocation();
    const navigate = useNavigate();
    const routeQuery = useRouteQuery();

    const localStorageView = localStorage.getItem(STORAGE_KEY);
    const currentRouteView = routeQuery.get('view');
    const viewParam = currentRouteView || localStorageView;
    const isGridView = !viewParam || viewParam === EView.Grid;

    const navigateToView = useCallback(
        (view: EView) => {
            const newSearchParams = new URLSearchParams(window.location.search);

            if (newSearchParams.get('view') === view) return;

            newSearchParams.set('view', view);

            navigate(
                { ...location, search: `?${newSearchParams.toString()}` },
                { replace: true }
            );
        },
        [location, navigate]
    );

    const setLocalStorageView = (view: EView) => {
        localStorage.setItem(STORAGE_KEY, view);
        navigateToView(view);
    };

    useEffect(() => {
        if (!!localStorageView && !currentRouteView) {
            navigateToView((localStorageView as EView) || EView.Grid);
        }
    }, [localStorageView, currentRouteView, navigateToView]);

    const viewToggle = (
        <ViewToggle
            value={isGridView ? EView.Grid : EView.List}
            onClickButton={(key) => {
                setLocalStorageView(key);
            }}
        />
    );

    const userId = userIdParam || data?.currentUser?.id;

    const {
        skillGroups,
        skillGroupsCount,
        loading: skillGroupsLoading,
        paginationSettings,
        setPage,
    } = useSkillGroupsList({
        variables: { userIds: userId ? [userId] : undefined },
        skip: !userId,
    });

    const skillGroupIds = skillGroups.map((group) => group.id);
    const count = skillGroupsCount || 0;

    const { items: skillPortfolioItems, loading: skillsLoading } =
        useSkillReportList({
            queryVariables: {
                userIds: userId ? [userId] : [],
                skillIds: skillGroups.flatMap(
                    (group) =>
                        group.skillRequirements
                            ?.map((req) => req.skill?.id)
                            .filter(Boolean) ?? []
                ),
                withTitle: true,
            },
            skip: !userId || !skillGroupIds.length,
        });

    const loading = skillGroupsLoading || skillsLoading;

    const groups = useMemo(
        () =>
            skillGroups.map((group) => {
                const { skillRequirements } = group;

                const skillItems = skillRequirements
                    ?.map((req) => {
                        if (!req.skill) return null;

                        const portfolioItem = skillPortfolioItems.find(
                            (item) => item.relatedId === req.skill!.id
                        ) as IPortfolioItemMatrixListItemFragment | undefined;

                        return {
                            skill: req.skill,
                            portfolioItem,
                        };
                    })
                    .filter(Boolean);

                return {
                    ...group,
                    skills: skillItems,
                };
            }),
        [skillGroups, skillPortfolioItems]
    );

    const handleSkillClick = (portfolioItemId: string | undefined) => {
        openGlobalDrawer(
            {
                type: IGlobalDrawerType.PortfolioItem,
                itemId: portfolioItemId,
            },
            location
        );
    };

    return (
        <>
            <PageTitle>{`${translate('portfolioSkillsPage.pageTitle')} - ${name}`}</PageTitle>

            {!loading && (
                <Box sx={{ mt: 3 }}>
                    {groups.map(
                        ({ id, name, extraCategoryValues, skills }, index) => {
                            if (!skills?.length) return null;

                            return (
                                <Box key={`group-${id}`} sx={{ mb: 4 }}>
                                    <Box sx={{ position: 'relative', mb: 1 }}>
                                        {index === 0 && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '-8px',
                                                    right: 0,
                                                }}
                                            >
                                                {viewToggle}
                                            </Box>
                                        )}

                                        <Typography variant="h4">
                                            {name}
                                        </Typography>
                                    </Box>

                                    {extraCategoryValues && (
                                        <ChipGroup sx={{ mb: 2 }}>
                                            <ExtraCategoryLabels
                                                extraCategoryValues={
                                                    extraCategoryValues
                                                }
                                            />
                                        </ChipGroup>
                                    )}

                                    {isGridView ? (
                                        <Grid container spacing={3}>
                                            {skills.map((skillItem) => {
                                                const { skill, portfolioItem } =
                                                    skillItem;

                                                return (
                                                    <Grid
                                                        key={`skill-${skill.id}`}
                                                        size={{
                                                            md: 4,
                                                            sm: 6,
                                                            xs: 12,
                                                        }}
                                                    >
                                                        <SkillCard
                                                            portfolioItem={
                                                                portfolioItem
                                                            }
                                                            skill={skill}
                                                            onClick={() => {
                                                                handleSkillClick(
                                                                    portfolioItem?.id
                                                                );
                                                            }}
                                                        />
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    ) : (
                                        <List noBorder noPadding>
                                            {skills.map((skillItem) => {
                                                const { skill, portfolioItem } =
                                                    skillItem;

                                                return (
                                                    <SkillPortfolioItemListItem
                                                        key={`skill-${skill.id}`}
                                                        portfolioItem={
                                                            portfolioItem
                                                        }
                                                        skill={skill}
                                                        onClick={() => {
                                                            handleSkillClick(
                                                                portfolioItem?.id
                                                            );
                                                        }}
                                                    />
                                                );
                                            })}
                                        </List>
                                    )}
                                </Box>
                            );
                        }
                    )}
                </Box>
            )}

            {loading && <BoxLoader />}

            {!loading && count === 0 && <NoResults variant="SKILLS" />}

            {!loading && count > DEFAULT_PAGINATION_SETTINGS.first && (
                <Pagination
                    page={paginationSettings.page}
                    pageAmount={paginationSettings.pageAmount}
                    totalsAmount={count}
                    totalsText={translate('result', {
                        count: count || 0,
                    })}
                    onChange={(page: number) => {
                        setPage(page);
                    }}
                />
            )}
        </>
    );
};
